import { EMPTY_LIST } from '../../constants/common'

import {
  WYSIWYG_EDITOR_DEFAULT_CARET_SCROLL_MARGIN_BOTTOM,
  WYSIWYG_EDITOR_OVERFLOW_HIDDEN,
  WYSIWYG_EDITOR_SCROLLABLE_OVERFLOW_REGEXP,
} from './WysiwygEditor.constants'
import { getWysiwygEditorStickyScrollDelta } from './WysiwygEditor.utils.sticky'

function getElementAncestors(element) {
  const parent = element.parentElement

  return parent ? [parent, ...getElementAncestors(parent)] : EMPTY_LIST
}

function isScrollableElement(element) {
  return (
    WYSIWYG_EDITOR_SCROLLABLE_OVERFLOW_REGEXP.test(
      getComputedStyle(element).overflowY
    ) && element.scrollHeight > element.clientHeight
  )
}

function isEditorBodyScrollEnabled(editor) {
  return editor.getBody().style.overflowY !== WYSIWYG_EDITOR_OVERFLOW_HIDDEN
}

// the iframe document scrolls once `autoresize` hits `max_height`
function getEditorContentScrollingElement(editor) {
  const { scrollingElement } = editor.getDoc()

  return isEditorBodyScrollEnabled(editor) &&
    scrollingElement.scrollHeight > scrollingElement.clientHeight
    ? [scrollingElement]
    : EMPTY_LIST
}

function getEditorScrollParents(editor) {
  return [
    ...getEditorContentScrollingElement(editor),
    ...getElementAncestors(editor.getContainer()).filter(isScrollableElement),
  ]
}

function getEditorScrollParentRect(editor, scrollParent) {
  return (
    scrollParent === editor.getDoc().scrollingElement
      ? editor.iframeElement
      : scrollParent
  ).getBoundingClientRect()
}

function getEditorViewportRect(editor, rect) {
  const iframeRect = editor.iframeElement.getBoundingClientRect()

  return {
    bottom: iframeRect.top + rect.bottom,
    left: iframeRect.left + rect.left,
    right: iframeRect.left + rect.right,
    top: iframeRect.top + rect.top,
  }
}

function isRectOverlapping(firstRect, secondRect) {
  return (
    firstRect.top < secondRect.bottom &&
    firstRect.bottom > secondRect.top &&
    firstRect.left < secondRect.right &&
    firstRect.right > secondRect.left
  )
}

function getRectOverflowDelta({ marginBottom, parentRect, rect }) {
  const overflowBottom = rect.bottom - (parentRect.bottom - marginBottom)
  const overflowTop = rect.top - parentRect.top

  return overflowBottom > 0 ? overflowBottom : Math.min(overflowTop, 0)
}

function scrollElementBy(element, delta) {
  const prevScrollTop = element.scrollTop

  element.scrollTop += delta

  return element.scrollTop - prevScrollTop
}

function scrollEditorParentsBy(editor, delta) {
  getEditorScrollParents(editor).reduce(
    (restDelta, scrollParent) =>
      restDelta - scrollElementBy(scrollParent, restDelta),
    delta
  )
}

function scrollEditorRectIntoView({ editor, marginBottom, rect }) {
  getEditorScrollParents(editor).reduce((restRect, scrollParent) => {
    const appliedDelta = scrollElementBy(
      scrollParent,
      getRectOverflowDelta({
        marginBottom,
        parentRect: getEditorScrollParentRect(editor, scrollParent),
        rect: restRect,
      })
    )

    return {
      bottom: restRect.bottom - appliedDelta,
      top: restRect.top - appliedDelta,
    }
  }, rect)
}

function scrollEditorRectFromUnderSticky({ editor, rect }) {
  scrollEditorParentsBy(
    editor,
    getWysiwygEditorStickyScrollDelta({ editor, rect })
  )
}

export function getWysiwygEditorCaretRect(editor) {
  const [rangeRect] = editor.selection.getRng().getClientRects()

  return getEditorViewportRect(
    editor,
    rangeRect || editor.selection.getNode().getBoundingClientRect()
  )
}

export function isWysiwygEditorCaretOverElement(editor, element) {
  return isRectOverlapping(
    getWysiwygEditorCaretRect(editor),
    element.getBoundingClientRect()
  )
}

export function scrollWysiwygEditorCaretFromUnderSticky(editor) {
  if (editor.selection.isCollapsed()) {
    scrollEditorRectFromUnderSticky({
      editor,
      rect: getWysiwygEditorCaretRect(editor),
    })
  }
}

export function scrollWysiwygEditorCaretIntoView({
  editor,
  marginBottom = WYSIWYG_EDITOR_DEFAULT_CARET_SCROLL_MARGIN_BOTTOM,
}) {
  scrollEditorRectIntoView({
    editor,
    marginBottom,
    rect: getWysiwygEditorCaretRect(editor),
  })

  scrollWysiwygEditorCaretFromUnderSticky(editor)
}

export function scrollWysiwygEditorElementIntoView({
  editor,
  element,
  marginBottom = WYSIWYG_EDITOR_DEFAULT_CARET_SCROLL_MARGIN_BOTTOM,
}) {
  scrollEditorRectIntoView({
    editor,
    marginBottom,
    rect: getEditorViewportRect(editor, element.getBoundingClientRect()),
  })

  scrollEditorRectFromUnderSticky({
    editor,
    rect: getEditorViewportRect(editor, element.getBoundingClientRect()),
  })
}

export function restoreWysiwygEditorCaretViewportTop({ editor, top }) {
  scrollEditorParentsBy(editor, getWysiwygEditorCaretRect(editor).top - top)
}
